import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'

import ErrorBoundary from 'components/Common/ErrorBoundary'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import type { MarkerSelectionData } from 'tripPlanner/components/Chat/chatMapConstants'
import type { TripStop } from 'tripPlanner/api/types'
import { MARKER_TYPE_STOP } from 'tripPlanner/utils/mapMarkerTransforms'
import { useEventSubscription, useTripPlannerEventBus, type TripPlannerEvent } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'

import PlaceInfoDrawer from '../components/PlaceInfoDrawer'
import { useChatActions } from '../contexts/ChatActionsContext'
import { useItineraryContext } from '../contexts/ItineraryContext'
import type { RightPanelView } from '../hooks/useRightPanelState'
import type { TripChatMobileTabId } from './TripChatMobileTabs'
import { useTripChatMobileAppShell } from './TripChatMobileAppShell'

const LazyMapContainer = lazy(() => import('../containers/MapContainer'))

type MapOpeningEventType =
  | 'show_hotel_on_map'
  | 'show_experience_on_map'
  | 'show_offer_suggestion_on_map'
  | 'show_place_on_map'
  | 'show_chat_map_on_map'
  | 'show_cruise_transport_on_map'
  | 'show_explore_on_map'
  | 'ui_action'

const MAP_OPENING_EVENT_TYPES: Array<MapOpeningEventType> = [
  'show_hotel_on_map',
  'show_experience_on_map',
  'show_offer_suggestion_on_map',
  'show_place_on_map',
  'show_chat_map_on_map',
  'show_cruise_transport_on_map',
  'show_explore_on_map',
  'ui_action',
]

type MapOpeningEvent = Extract<TripPlannerEvent, { type: MapOpeningEventType }>

interface MobileMapContainerProps {
  visible: boolean
  selectedDayItineraryId: number | undefined
  selectedStop: TripStop | undefined
  selectedMarkerData: MarkerSelectionData | undefined
  onMarkerSelect: (data: MarkerSelectionData | undefined) => void
  setRightPanelView: Dispatch<SetStateAction<RightPanelView>>
  rightPanelView: RightPanelView
  onMounted: () => VoidFunction | undefined
}

const MapRoot = styled.div`
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
`

const MapLoadingFallback = styled.div`
  display: flex;
  height: 100%;
  min-height: ${rem(240)};
  align-items: center;
  justify-content: center;
`

function getMobileRightPanelView(activeTab: TripChatMobileTabId): RightPanelView {
  if (activeTab === 'list') return 'list'
  return 'map'
}

function getMobileTabForRightPanelView(view: RightPanelView): TripChatMobileTabId | undefined {
  if (view === 'map' || view === 'list') return view
  return undefined
}

function shouldOpenMapForEvent(event: MapOpeningEvent) {
  if (event.type !== 'ui_action') return true

  return event.data.action === 'set_right_panel_view' && event.data.view === 'map'
}

function MobileMapContainer({
  onMounted,
  ...mapContainerProps
}: MobileMapContainerProps) {
  useEffect(() => onMounted(), [onMounted])

  return <LazyMapContainer {...mapContainerProps} />
}

function TripChatMobileMapView() {
  const eventBus = useTripPlannerEventBus()
  const { activeTab, setActiveTab } = useTripChatMobileAppShell()
  const { sendMessage } = useChatActions()
  const { itineraryData } = useItineraryContext()
  const [hasOpenedMap, setHasOpenedMap] = useState(activeTab === 'map')
  const [selectedMarkerData, setSelectedMarkerData] = useState<MarkerSelectionData | undefined>(undefined)
  const pendingMapOpeningEventRef = useRef<MapOpeningEvent | undefined>(undefined)
  const isMapActive = activeTab === 'map'
  const rightPanelView = getMobileRightPanelView(activeTab)
  const shouldRenderMapContainer = isMapActive || hasOpenedMap

  useEffect(() => {
    if (isMapActive) {
      setHasOpenedMap(true)
      return
    }

    setSelectedMarkerData(undefined)
  }, [isMapActive])

  // List tab switches live on TripChatMobileAppShell (always mounted).
  // This view only opens the map tab for map-bound ui_action / map commands.
  useEventSubscription<MapOpeningEventType>(MAP_OPENING_EVENT_TYPES, (event) => {
    if (isMapActive || !shouldOpenMapForEvent(event)) return

    if (hasOpenedMap) {
      setActiveTab('map')
      return
    }

    pendingMapOpeningEventRef.current = event
    setActiveTab('map')
  })

  const replayPendingMapOpeningEvent = useCallback(() => {
    if (!isMapActive || !pendingMapOpeningEventRef.current) return undefined

    const event = pendingMapOpeningEventRef.current
    pendingMapOpeningEventRef.current = undefined
    const replayTimer = window.setTimeout(() => {
      eventBus.dispatch(event)
    }, 0)

    return () => window.clearTimeout(replayTimer)
  }, [eventBus, isMapActive])

  const setMobileRightPanelView = useCallback<Dispatch<SetStateAction<RightPanelView>>>((nextView) => {
    const resolvedView = typeof nextView === 'function'
      ? nextView(getMobileRightPanelView(activeTab))
      : nextView

    if (resolvedView === 'cart') {
      eventBus.dispatch({ type: 'open_cart_panel' })
      return
    }

    const nextTab = getMobileTabForRightPanelView(resolvedView)
    if (nextTab) {
      setActiveTab(nextTab)
    }
  }, [activeTab, eventBus, setActiveTab])

  const handleMarkerOverlayClose = useCallback(() => {
    setSelectedMarkerData(undefined)
  }, [])

  const mapContainer = useMemo(() => {
    if (!shouldRenderMapContainer) return undefined

    return (
      <Suspense
        fallback={(
          <MapLoadingFallback data-testid="trip-chat-mobile-map-loading">
            <LoadingAnimation size={24} />
          </MapLoadingFallback>
        )}
      >
        <ErrorBoundary fallback={null}>
          <MobileMapContainer
            visible={isMapActive}
            selectedDayItineraryId={undefined}
            selectedStop={undefined}
            selectedMarkerData={selectedMarkerData}
            onMarkerSelect={setSelectedMarkerData}
            setRightPanelView={setMobileRightPanelView}
            rightPanelView={rightPanelView}
            onMounted={replayPendingMapOpeningEvent}
          />
        </ErrorBoundary>
      </Suspense>
    )
  }, [
    isMapActive,
    replayPendingMapOpeningEvent,
    rightPanelView,
    selectedMarkerData,
    setMobileRightPanelView,
    shouldRenderMapContainer,
  ])

  return (
    <MapRoot data-testid="trip-chat-mobile-map-view">
      {mapContainer}
      {selectedMarkerData?.marker.placeId && (
        <PlaceInfoDrawer
          isOpen={Boolean(selectedMarkerData)}
          onClose={handleMarkerOverlayClose}
          googlePlaceId={selectedMarkerData.marker.placeId}
          placeName={selectedMarkerData.marker.title || selectedMarkerData.marker.label || ''}
          itineraryData={itineraryData}
          onSendMessage={sendMessage}
          hideAddToTrip={selectedMarkerData.marker.markerType === MARKER_TYPE_STOP}
        />
      )}
    </MapRoot>
  )
}

export default TripChatMobileMapView
